'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph } from '../../../internal/icons.js';
/** Returns a copy of `list` with the item at `from` moved to `to`. */
export function moveItem(list, from, to) {
    const next = list.slice();
    if (from < 0 || from >= next.length)
        return next;
    const target = Math.max(0, Math.min(to, next.length - 1));
    const [item] = next.splice(from, 1);
    next.splice(target, 0, item);
    return next;
}
const DEFAULT_MESSAGES = {
    handleLabel: (label) => `Reorder ${label}`,
    instructions: 'Press Space or Enter to pick up. Use the arrow keys to move, Space or Enter to drop, Escape to cancel.',
    moveUp: (label) => `Move ${label} up`,
    moveDown: (label) => `Move ${label} down`,
    pickedUp: (label, p, n) => `Picked up ${label}. Position ${p} of ${n}.`,
    moved: (label, p, n) => `${label} moved to position ${p} of ${n}.`,
    dropped: (label, p, n) => `${label} dropped at position ${p} of ${n}.`,
    cancelled: (label, p, n) => `Reorder cancelled. ${label} returned to position ${p} of ${n}.`,
};
const NBSP = String.fromCharCode(160);
/** Pixels a pointer must travel before a press becomes a drag. */
const DRAG_THRESHOLD = 4;
function GripGlyph() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "currentColor", "aria-hidden": true, focusable: false, children: [_jsx("circle", { cx: "6", cy: "4", r: "1.25" }), _jsx("circle", { cx: "10", cy: "4", r: "1.25" }), _jsx("circle", { cx: "6", cy: "8", r: "1.25" }), _jsx("circle", { cx: "10", cy: "8", r: "1.25" }), _jsx("circle", { cx: "6", cy: "12", r: "1.25" }), _jsx("circle", { cx: "10", cy: "12", r: "1.25" })] }));
}
function reducedMotion() {
    return (typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}
function ReorderableListInner({ items: itemsProp, defaultItems, onReorder, getKey, getLabel, renderItem, isItemDisabled, disabled = false, moveButtons = false, messages: messagesProp, className, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const id = useId();
    const instructionsId = `mn-reorderable-${id}-instructions`;
    const [internal, setInternal] = useState(defaultItems ?? []);
    const items = itemsProp ?? internal;
    const [drag, setDrag] = useState(null);
    const dragRef = useRef(null);
    dragRef.current = drag;
    /** The order shown while a move is in progress. */
    const [preview, setPreview] = useState(null);
    const shown = preview ?? items;
    const [announcement, setAnnouncement] = useState('');
    const listRef = useRef(null);
    const nodes = useRef(new Map());
    const handles = useRef(new Map());
    const lastTops = useRef(new Map());
    const focusAfter = useRef(null);
    const announce = useCallback((text) => {
        // Re-announce identical text by clearing first.
        setAnnouncement((prev) => (prev === text ? `${text}${NBSP}` : text));
    }, []);
    const commit = useCallback((next, move) => {
        if (itemsProp === undefined)
            setInternal(next);
        onReorder?.(next, move);
    }, [itemsProp, onReorder]);
    const indexOfKey = (list, key) => list.findIndex((i) => getKey(i) === key);
    const isLocked = (item) => disabled || (isItemDisabled?.(item) ?? false);
    // FLIP: rows that change place glide to it instead of jumping.
    useLayoutEffect(() => {
        const animate = !reducedMotion();
        const current = dragRef.current;
        for (const [key, node] of nodes.current) {
            const top = node.offsetTop;
            const before = lastTops.current.get(key);
            lastTops.current.set(key, top);
            if (!animate || before === undefined || before === top)
                continue;
            if (current?.mode === 'pointer' && current.key === key)
                continue;
            node.style.transition = 'none';
            node.style.transform = `translateY(${before - top}px)`;
            void node.offsetHeight;
            node.style.transition = '';
            node.style.transform = '';
        }
        // The dragged row follows the pointer from wherever the layout put it.
        if (current?.mode === 'pointer') {
            const node = nodes.current.get(current.key);
            if (node && current.originTop !== undefined)
                node.style.transform = `translateY(${current.originTop + (current.dy ?? 0) - node.offsetTop}px)`;
        }
        if (focusAfter.current) {
            const { key, target } = focusAfter.current;
            focusAfter.current = null;
            const row = nodes.current.get(key);
            const el = target === 'handle'
                ? handles.current.get(key)
                : (row?.querySelector(`[data-move='${target}']:not(:disabled)`) ??
                    handles.current.get(key));
            if (el && document.activeElement !== el)
                el.focus();
        }
    });
    const endDrag = () => {
        const d = dragRef.current;
        if (d) {
            const node = nodes.current.get(d.key);
            if (node)
                node.style.transform = '';
        }
        dragRef.current = null;
        setDrag(null);
        setPreview(null);
    };
    const finish = (cancel) => {
        const d = dragRef.current;
        if (!d)
            return;
        const list = preview ?? items;
        const to = indexOfKey(list, d.key);
        const item = items[d.from];
        if (item === undefined)
            return endDrag();
        const label = getLabel(item);
        if (cancel || to === d.from || to === -1) {
            announce(cancel
                ? messages.cancelled(label, d.from + 1, items.length)
                : messages.dropped(label, d.from + 1, items.length));
        }
        else {
            announce(messages.dropped(label, to + 1, items.length));
            commit(list.slice(), { item, from: d.from, to });
        }
        if (d.mode === 'keyboard')
            focusAfter.current = { key: d.key, target: 'handle' };
        else if (d.originTop !== undefined)
            lastTops.current.set(d.key, d.originTop + (d.dy ?? 0));
        endDrag();
    };
    // A move abandoned by losing the item (items changed under us) ends quietly.
    useEffect(() => {
        if (drag && indexOfKey(items, drag.key) === -1)
            endDrag();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);
    const startKeyboard = (item) => {
        const key = getKey(item);
        const from = indexOfKey(items, key);
        const d = { key, from, mode: 'keyboard' };
        dragRef.current = d;
        setDrag(d);
        setPreview(items);
        announce(messages.pickedUp(getLabel(item), from + 1, items.length));
    };
    const moveKeyboard = (to) => {
        const d = dragRef.current;
        if (!d)
            return;
        const list = preview ?? items;
        const from = indexOfKey(list, d.key);
        const target = Math.max(0, Math.min(to, list.length - 1));
        const item = list[from];
        if (item === undefined)
            return;
        if (target !== from) {
            setPreview(moveItem(list, from, target));
            focusAfter.current = { key: d.key, target: 'handle' };
        }
        announce(messages.moved(getLabel(item), target + 1, list.length));
    };
    const onHandleKeyDown = (e, item) => {
        const d = dragRef.current;
        const key = getKey(item);
        const grabbed = d?.mode === 'keyboard' && d.key === key;
        const list = preview ?? items;
        const index = indexOfKey(list, key);
        switch (e.key) {
            case ' ':
            case 'Enter':
                e.preventDefault();
                if (e.repeat)
                    return;
                if (grabbed)
                    finish(false);
                else if (!d)
                    startKeyboard(item);
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
                if (grabbed) {
                    e.preventDefault();
                    moveKeyboard(index - 1);
                }
                break;
            case 'ArrowDown':
            case 'ArrowRight':
                if (grabbed) {
                    e.preventDefault();
                    moveKeyboard(index + 1);
                }
                break;
            case 'Home':
                if (grabbed) {
                    e.preventDefault();
                    moveKeyboard(0);
                }
                break;
            case 'End':
                if (grabbed) {
                    e.preventDefault();
                    moveKeyboard(list.length - 1);
                }
                break;
            case 'Escape':
                if (d) {
                    e.preventDefault();
                    e.stopPropagation();
                    finish(true);
                }
                break;
            case 'Tab':
                if (grabbed)
                    finish(true);
                break;
        }
    };
    /** A press on a handle that has not moved far enough to be a drag yet. */
    const pending = useRef(null);
    const startPointer = (item, pointerId, startY) => {
        const key = getKey(item);
        const node = nodes.current.get(key);
        if (!node)
            return;
        const centres = new Map();
        for (const [k, n] of nodes.current)
            centres.set(k, n.offsetTop + n.offsetHeight / 2);
        const d = {
            key,
            from: indexOfKey(items, key),
            mode: 'pointer',
            pointerId,
            startY,
            dy: 0,
            centres,
            originTop: node.offsetTop,
        };
        dragRef.current = d;
        setDrag(d);
        setPreview(items);
        announce(messages.pickedUp(getLabel(item), d.from + 1, items.length));
    };
    const onPointerDown = (e, item) => {
        if (dragRef.current || e.button !== 0)
            return;
        // Keep the page from scrolling or selecting text; focus the handle ourselves.
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        e.currentTarget.focus({ preventScroll: true });
        pending.current = { item, pointerId: e.pointerId, x: e.clientX, y: e.clientY };
    };
    const onPointerMove = (e) => {
        const p = pending.current;
        if (p && p.pointerId === e.pointerId) {
            if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_THRESHOLD)
                return;
            pending.current = null;
            startPointer(p.item, p.pointerId, p.y);
        }
        const d = dragRef.current;
        if (!d || d.mode !== 'pointer' || d.pointerId !== e.pointerId)
            return;
        const dy = e.clientY - (d.startY ?? 0);
        d.dy = dy;
        const node = nodes.current.get(d.key);
        const centre = (d.centres?.get(d.key) ?? 0) + dy;
        // The new slot: how many other rows' original centres lie above the dragged centre.
        let to = 0;
        for (const item of items) {
            const k = getKey(item);
            if (k !== d.key && (d.centres?.get(k) ?? 0) < centre)
                to++;
        }
        const list = preview ?? items;
        const from = indexOfKey(list, d.key);
        if (to !== from) {
            setPreview(moveItem(list, from, to));
            const item = list[from];
            if (item !== undefined)
                announce(messages.moved(getLabel(item), to + 1, list.length));
        }
        else if (node && d.originTop !== undefined) {
            node.style.transform = `translateY(${d.originTop + dy - node.offsetTop}px)`;
        }
    };
    const onPointerEnd = (e, cancel) => {
        if (pending.current?.pointerId === e.pointerId)
            pending.current = null;
        const d = dragRef.current;
        if (!d || d.mode !== 'pointer' || d.pointerId !== e.pointerId)
            return;
        finish(cancel);
    };
    const moveByButton = (item, delta) => {
        const from = indexOfKey(items, getKey(item));
        const to = from + delta;
        if (to < 0 || to >= items.length)
            return;
        const next = moveItem(items, from, to);
        announce(messages.moved(getLabel(item), to + 1, items.length));
        focusAfter.current = { key: getKey(item), target: delta < 0 ? 'up' : 'down' };
        commit(next, { item, from, to });
    };
    return (_jsxs("div", { className: "mn-reorderable-list", "data-dragging": drag ? drag.mode : undefined, children: [_jsx("ul", { ref: (node) => {
                    listRef.current = node;
                    if (typeof ref === 'function')
                        ref(node);
                    else if (ref)
                        ref.current = node;
                }, ...rest, className: cx('mn-reorderable-list__list', className), children: shown.map((item, index) => {
                    const key = getKey(item);
                    const label = getLabel(item);
                    const locked = isLocked(item);
                    const active = drag?.key === key;
                    return (_jsxs("li", { ref: (node) => {
                            if (node)
                                nodes.current.set(key, node);
                            else {
                                nodes.current.delete(key);
                                handles.current.delete(key);
                                lastTops.current.delete(key);
                            }
                        }, className: "mn-reorderable-list__item", "data-active": active ? drag?.mode : undefined, "data-disabled": locked || undefined, children: [_jsx("button", { ref: (node) => {
                                    if (node)
                                        handles.current.set(key, node);
                                }, type: "button", className: "mn-reorderable-list__handle", "aria-label": messages.handleLabel(label), "aria-describedby": instructionsId, "aria-pressed": active && drag?.mode === 'keyboard', disabled: locked, onKeyDown: (e) => onHandleKeyDown(e, item), onBlur: (e) => {
                                    // Focus leaving the list during a keyboard move cancels it.
                                    const next = e.relatedTarget;
                                    if (dragRef.current?.mode === 'keyboard' &&
                                        next &&
                                        !listRef.current?.contains(next))
                                        finish(true);
                                }, onPointerDown: (e) => onPointerDown(e, item), onPointerMove: onPointerMove, onPointerUp: (e) => onPointerEnd(e, false), onPointerCancel: (e) => onPointerEnd(e, true), onLostPointerCapture: (e) => onPointerEnd(e, false), children: _jsx(GripGlyph, {}) }), _jsx("div", { className: "mn-reorderable-list__content", children: renderItem(item, { index, active, disabled: locked }) }), moveButtons ? (_jsxs("span", { className: "mn-reorderable-list__moves", children: [_jsx("button", { type: "button", className: "mn-reorderable-list__move", "data-move": "up", "aria-label": messages.moveUp(label), disabled: locked || index === 0 || drag !== null, onClick: () => moveByButton(item, -1), children: _jsx(ChevronDownGlyph, { style: { transform: 'rotate(180deg)' } }) }), _jsx("button", { type: "button", className: "mn-reorderable-list__move", "data-move": "down", "aria-label": messages.moveDown(label), disabled: locked || index === shown.length - 1 || drag !== null, onClick: () => moveByButton(item, 1), children: _jsx(ChevronDownGlyph, {}) })] })) : null] }, key));
                }) }), _jsx("span", { id: instructionsId, className: "mn-visually-hidden", children: messages.instructions }), _jsx("span", { className: "mn-visually-hidden", "aria-live": "assertive", "aria-atomic": "true", children: announcement })] }));
}
/**
 * A list the user can reorder by dragging a handle (mouse, pen or touch) or
 * from the keyboard: Space picks an item up, the arrow keys move it, Space
 * drops it and Escape cancels. Every step is announced. Optional move buttons
 * give a one-click alternative.
 */
export const ReorderableList = forwardRef(ReorderableListInner);
//# sourceMappingURL=reorderable-list.js.map